import { AuthProvider, useAuth } from './providers/auth-provider';
import { ThemeProvider } from './providers/theme-provider';
import { LoginScreen } from './components/LoginScreen';
import { Workspace } from './shell/Workspace';
import type { Theme } from './shell/types';

export interface ShadowAppProps {
  /**
   * Pre-minted auth token (embed mode). When supplied, the app skips the
   * login screen and attempts to hydrate the session from the token.
   */
  token?: string | null | undefined;
  /** Pre-filled email (MFA mode). Forces the code step before entry. */
  email?: string | null | undefined;
  /** Absolute API base URL (embed mode). Omit for same-origin `/api`. */
  apiUrl?: string | null | undefined;
  /** Optional tenant scoping attribute (forwarded from the custom element). */
  tenantId?: string | null | undefined;
  /** View-variant attribute (reserved for future embed modes like "chat" / "tasks"). */
  type?: string | null | undefined;
  /** JSON-encoded token overrides (parsed by ThemeProvider in Phase 2+). */
  dataStyle?: string | null | undefined;
  /** Initial theme: "light" | "dark". */
  dataTheme?: string | null | undefined;
  /**
   * Element that carries the `theme-light` / `theme-dark` class. In SPA
   * mode this is omitted (defaults to `document.documentElement`). In
   * Shadow DOM embed mode it's the `.sd-root` host child so the theme
   * doesn't leak onto the embedding page.
   */
  themeHostElement?: Element | null | undefined;
  /**
   * EventTarget for Workspace global keyboard shortcuts. SPA: window
   * (default). Embed: the `<shift-agent>` host element.
   */
  keyTarget?: EventTarget | undefined;
}

function AppContent({ keyTarget }: { keyTarget?: EventTarget | undefined }) {
  const { status, configError, externalEmail } = useAuth();

  if (configError) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: 'var(--bg)',
          color: 'var(--kind-failure)',
          padding: 24,
          fontSize: 13,
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", Inter, sans-serif',
        }}
      >
        {configError}
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div
        role="status"
        aria-label="Loading"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: 'var(--bg)',
          color: 'var(--text-soft)',
          fontSize: 12,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", Inter, sans-serif',
        }}
      >
        Loading…
      </div>
    );
  }

  if (status === 'idle' || status === 'error') {
    return <LoginScreen externalEmail={externalEmail} />;
  }

  return <Workspace keyTarget={keyTarget} />;
}

function coerceTheme(t: string | null | undefined): Theme | undefined {
  return t === 'light' || t === 'dark' ? t : undefined;
}

export function ShadowApp({
  token,
  email,
  apiUrl,
  dataTheme,
  themeHostElement,
  keyTarget,
}: ShadowAppProps = {}) {
  return (
    <ThemeProvider
      dataTheme={coerceTheme(dataTheme) ?? null}
      themeHostElement={themeHostElement ?? null}
    >
      <AuthProvider externalToken={token ?? null} externalEmail={email ?? null} apiBaseUrl={apiUrl ?? null}>
        <AppContent keyTarget={keyTarget} />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default ShadowApp;
